import { use, useEffect, useRef, useState } from "react";
import { getPercentage } from "../utils/math";
import ExpandingTextArea from "./ExpandingTextArea";

export const FormState = {
    UNKNOWN: 0,
    CREATE: 1,
    VIEW: 2,
    UPDATE: 3
}

function checkErrors(validators, value) {
    let error = ""
    validators.reverse().forEach(validator => {
        if(!validator.func(value)) {
            error = validator.error
            return
        }
    })
    return error
}

export function submitForm(form) {
    let isValid = true
    let data = {}
    Object.entries(form).forEach(([key, input]) => {
        const state = input.onSubmit()
        if(!state.isValid) {
            isValid = false
            return
        }
        data[key] = state.value
    })
    if (isValid) return data
    else return null
}

export function clearForm(form) {
    Object.entries(form).forEach(([_, input]) => input.reset())
}

export const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const required = {
    func: (val) => val && (val > 0 || val.length > 0),
    error: "This field is required."
}
export const email = {
    func: (val) => emailPattern.test(val),
    error: "Email format must be valid."
}
export const min = (target) => ({
    func: (val) => Number(val) >= target,
    error: `Value must be greater or equal ${target}.` 
})
export const max = (target) => ({
    func: (val) => Number(val) <= target,
    error: `Value must be smaller or equal ${target}.` 
})

export const useInput = (placeholder, icon, type, validators, initial, helpText) => {
    const [didSubmit, setDidSubmit] = useState(false)
    const [value, setValue] = useState(initial ?? "")
    const [error, setError] = useState("")
    const [enabled, setEnabled] = useState(true)
    const [initialUsed, setInitialUsed] = useState(false)
    const [_validators, setValidators] = useState(validators)

    const getSafeValue = () => {
        return initialUsed ? value : (value == "" ? (initial ?? "") : value)
    }

    const onSubmit = () => {
        setDidSubmit(true)
        let safeValue = getSafeValue()
        let err = checkErrors(_validators, safeValue) 
        setError(err)
        return {
            isValid: err.length == 0,
            value: safeValue
        }
    }

    const handleSetValue = (val) => {
        setValue(val)
        if (!initialUsed) setInitialUsed(true)
        if (didSubmit) setError(checkErrors(_validators, val))
    }

    const reset = () => {
        setDidSubmit(false)
        setError('')
        setValue('')
    }

    return {
        onSubmit,
        reset,
        setValidators,
        setEnabled,
        _didSubmit: didSubmit,
        _value: getSafeValue(), 
        _setValue: handleSetValue,
        _error: error,
        _config: {
            placeholder,
            type,
            icon,
            helpText,
            enabled
        }
    }
}

export const useTextInput = (placeholder, icon, validators, initial, helpText) => useInput(placeholder, icon, "text", validators, initial, helpText)
export const useNumberInput = (placeholder, icon, validators, initial, helpText) => useInput(placeholder, icon, "number", validators, initial, helpText)
export const useToggleInput = (placeholder, icon, isRequired, options, preselected, helpText) => {

    const input = useInput(placeholder, icon, "toggle", isRequired ? [required] : [], preselected, helpText)

    return {
        ...input,
        _config: {
            ...input._config,
            options: options
        }
    }
}
export const useRangeInput = (placeholder, icon, isRequired, rangeMin, rangeMax, step, preselected, helpText) => {

    const input = useInput(placeholder, icon, "range", isRequired ? [required] : [], String(preselected ?? rangeMin), helpText)

    const _onSumbit = () => {
        let resp = input.onSubmit()
        return {
            ...resp,
            value: Number(resp.value)
        }
    }
    
    return {
        ...input,
        onSubmit: _onSumbit,
        _config: {
            ...input._config,
            range: {
                min: rangeMin,
                max: rangeMax,
                step
            }
        }
    }
}
export const useAreaInput = (placeholder, icon, validators, minRows, maxRows, initial, helpText) =>  {
    const input = useInput(placeholder, icon, "area", validators, initial ?? '', helpText)
    return {
        ...input,
        _config: {
            ...input._config,
            minRows: minRows ?? 1,
            maxRows: maxRows ?? 10
        }
    } 
}

export default function Input({className, state}) {
    switch(state._config.type) {
        case 'toggle': return(<ToggleInput className={className} state={state}/>)
        case 'range': return(<RangeInput className={className} state={state}/>)
        case 'area': return(<TextAreaInput className={className} state={state}/>)
        case 'date': return(<DateInput className={className} state={state}/>)
        default: return(<RegularInput className={className} state={state}/>)
    }
}

function RegularInput({className, state}) {
    const [focused, setFocused] = useState(false)
    const [sensitive, setSensitive] = useState(false)
    const [type, setType] = useState('text')
    useEffect(() => {
        setSensitive(state._config.type === 'password')
        setType(state._config.type)
    }, [state._config.type])
    const toggleSensitive = () => {
        setType(prev => {
            if (!sensitive) return prev
            if (prev === 'text') return 'password'
            else return 'text' 
        })
    }

    return(
        <div className={className}>
            <div className={`input-wrapper regular-border ${state._error.length == 0 ? '' : 'error-border'} ${!state._config.enabled ? 'disabled-input' : ''}`}>
                {state._config.icon && <span className="material-symbols-outlined scale-sm input-icon">{state._config.icon}</span>}
                <input placeholder={state._config.placeholder} type={type} value={state._value} disabled={!state._config.enabled} onChange={(e) => state._setValue(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}/>
                {sensitive && <button className="icon" style={{transform: 'translate(4px, 0)'}} onClick={toggleSensitive}>
                    <span className="material-symbols-outlined scale-xs">{type == 'password' ? 'visibility' : 'visibility_off'}</span>
                </button>}
            </div>
            {focused && state._config.helpText && <p className="card-label neutral showing hi-spacer-xs vi-spacer-xs">{state._config.helpText}</p>}
            {state._config.enabled && <p className="card-label error showing hi-spacer-xs">{state._error}</p>}
        </div>
    )
}

function DateInput({className, state}) {
    const [focused, setFocused] = useState(false)
    const inputRef = useRef(null)
    const handleClick = () => {
        if (typeof inputRef?.current?.showPicker === 'function')
            inputRef?.current?.showPicker()
    }
    return(
        <div className={className}>
            <div className={`input-wrapper regular-border relative ${state._error.length == 0 ? '' : 'error-border'} ${!state._config.enabled ? 'disabled-input' : ''}`}>
                {state._config.icon && <span className="material-symbols-outlined scale-sm input-icon">{state._config.icon}</span>}
                {(!state._value || state._value == '') && <p className="card-body neutral hi-spacer-xs w-100 clickable" onClick={handleClick}>{state._config.placeholder}</p>}
                <input ref={inputRef} placeholder={state._config.placeholder} type={state._config.type} value={state._value} disabled={!state._config.enabled} style={{width: (!state._value || state._value == '') ? '38px' : '100%'}} onChange={(e) => state._setValue(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}/>
            </div>
            {focused && state._config.helpText && <p className="card-label neutral showing hi-spacer-xs vi-spacer-xs">{state._config.helpText}</p>}
            {state._config.enabled && <p className="card-label error showing hi-spacer-xs">{state._error}</p>}
        </div>
    )
}

function TextAreaInput({className, state}) {
    const [focused, setFocused] = useState(false)

    return(
        <div className={className}>
            <div className={`input-wrapper regular-border ${state._error.length == 0 ? '' : 'error-border'}`} style={{height: 'unset', alignItems: 'unset'}}>
                {state._config.icon && <span className="material-symbols-outlined scale-sm input-icon" style={{marginTop: '9px'}}>{state._config.icon}</span>}
                <ExpandingTextArea placeholder={state._config.placeholder} dynamicValue={state._value} onChange={state._setValue} minRows={state._config.minRows} maxRows={state._config.maxRows} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}/>
            </div>
            {focused && state._config.helpText && <p className="card-label neutral showing hi-spacer-xs vi-spacer-xs">{state._config.helpText}</p>}
            <p className="card-label error showing hi-spacer-xs">{state._error}</p>
        </div>
    )
}

function ToggleInput({className, state}) {
    return(
        <div className={className}>
            <div className={`input-wrapper regular-border ${state._error.length == 0 ? '' : 'error-border'}`} style={{minHeight: "50px", height: "unset"}}>
                {state._config.icon && <span className="material-symbols-outlined scale-sm input-icon">{state._config.icon}</span>}
                <div className='gap-xxs center wrap hi-spacer-xs w-100 padding-xs flex' style={{gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr)'}}>
                    {state._config.options.map(item => 
                        <button key={item.key} className={`${state._value == item.key ? 'solid' : 'elevated'}`} style={{flexGrow: '1'}} onClick={() => state._setValue(item.key)}>{item.label}</button>
                    )}
                </div>
            </div>
            <p className="card-label error showing hi-spacer-xs">{state._error}</p>
        </div>
    )
}

function RangeInput({className, state}) {

    const [focused, setFocused] = useState(false)
    const getPos = () => getPercentage(state._config.range.min, state._config.range.max, state._value)

    return(
        <div className={className}>
            <div className={`input-wrapper regular-border ${state._error.length == 0 ? '' : 'error-border'}`}>
                {state._config.icon && <span className="material-symbols-outlined scale-sm input-icon">{state._config.icon}</span>}
                <div className="w-100 relative hi-spacer-m">
                    <p className="slider-value medium upper-layer absolute" style={{left: `calc(${getPos()}% - 48px)`, translate: `${(100 - getPos()) * 48 / 100}px 0`}}>{getPos()}%</p>
                    <input className="slider" type="range" min={state._config.range.min} max={state._config.range.max} step={state._config.range.step} value={state._value} onChange={(e) => state._setValue(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}/>
                </div>
            </div>
            {focused && state._config.helpText && <p className="card-label neutral showing hi-spacer-xs vi-spacer-xs">{state._config.helpText}</p>}
            <p className="card-label error showing hi-spacer-xs">{state._error}</p>
        </div>
    )
} 

export function FileInput({className, onChange}) {

    const inputRef = useRef(null)
    const [file, setFile] = useState(null)

    useEffect(() => onChange(file), [file])

    const handleOpenFile = () => {
        inputRef.current.click()
    }
    
    const handleDrop = (e) => {
        e.preventDefault()
        const files = e.dataTransfer.files
        if (files.length > 0) {
            inputRef.current.files = files
            handleFileChange({ target: { files } })
        }
    }

    const handleDragOver = (e) => {  e.preventDefault() }

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0]
        if (selectedFile) {
            setFile(selectedFile)
        }
    }

    return(
        <div className={className}>
            <div className="regular-border standard-padding-s flex columns center gap-m" onDragOver={handleDragOver} onDrop={handleDrop}>
                <input type="file" ref={inputRef} onChange={handleFileChange} style={{display: 'none'}}/>
                <div className="flex columns center gap-xxs">
                    <p className="card-body regular">Drag & Drop File Here</p>
                    {file && <p className="card-body regular"><span className="neutral">to replace</span> <span className="medium">{file.name}</span></p>}
                </div>
                <div className="flex center gap-xs o-50">
                    <hr style={{width: '125px'}}/>
                    <p className="medium o-65">OR</p>
                    <hr style={{width: '125px'}}/>
                </div>
                <button className={`${file ? 'elevated' : 'solid'}`} onClick={handleOpenFile}>{file ? 'Change File' : 'Browse Files'}</button>
            </div>
        </div>
    )
}